import SessionContext from "../utils/SessionContext";
import ChatCache from "../utils/ChatCache";

const chatCache = new ChatCache();

/**
 * Static URL context (standalone widget: room number, floor…). Omitted entirely
 * when empty so an ordinary embedded request keeps exactly the body it had.
 */
const withSessionContext = (body: Record<string, any>) =>
  SessionContext.isEmpty()
    ? body
    : { ...body, session_context: SessionContext.get() };

const IFH_ASSISTANTS = [
  "10c2cb34-db5e-47f5-a98d-c64a6b9b41e7",
  "ad344bae-b705-48b2-a667-3a48428f7e97",
  "b820ee02-ac53-422b-9c76-2f5d1c53aa24",
];

export default class ChatService {
  D_ENGINE = "d-engine";
  BACKEND = "backend";

  public async sendMessage(
    userMessage: any,
    assistantId: any,
    thread_id: any,
    language: any
  ) {
    const baseUrl = this.getBaseUrl(assistantId, this.D_ENGINE);

    return await fetch(`${baseUrl}`, {
      method: "POST",
      headers: {
        "Access-Control-Allow-Origin": "*",
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(
        withSessionContext({
          drastik_assistant_id: assistantId,
          user_message: userMessage,
          thread_id: thread_id ?? null,
          language: language,
        })
      ),
    });
  }

  /**
   * Single handoff endpoint (F1): persists the contact, sets the redirected
   * status and, when the property has email handoff enabled, opens an email
   * thread so the confirmation comes back by email.
   */
  public async handoff(
    assistantId: any,
    threadId: any,
    contact: { email?: string; phone?: string; name?: string }
  ) {
    const baseUrl = this.getBaseUrl(assistantId, this.BACKEND);

    return await fetch(`${baseUrl}Chats/handoff`, {
      method: "POST",
      headers: this.backendHeaders(assistantId),
      body: JSON.stringify({ assistantId, threadId, contact }),
    });
  }

  /**
   * Legacy handoff endpoints. F1 routes the UI through `handoff` above, but the
   * backend still exposes these two and every Chats/* call must carry the same
   * widget-token contract.
   */
  public async sendMessageWithoutReply(
    userMessage: any,
    assistantId: any,
    thread_id: any,
    contact?: {
      firstName?: string;
      lastName?: string;
      email?: string;
      phone?: string;
    }
  ) {
    const baseUrl = this.getBaseUrl(assistantId, this.BACKEND);

    return await fetch(`${baseUrl}chats/sendMessageWithoutReply`, {
      method: "POST",
      headers: this.backendHeaders(assistantId),
      body: JSON.stringify({
        drastikAssistantId: assistantId,
        message: userMessage,
        threadId: thread_id,
        firstName: contact?.firstName,
        lastName: contact?.lastName,
        email: contact?.email,
        phone: contact?.phone,
      }),
    });
  }

  public setRedirectedStatus(data: any) {
    const baseUrl = this.getBaseUrl(data.assistantId, this.BACKEND);

    let url = baseUrl + (process.env.REACT_APP_SET_REDIRECTED_STATUS ?? "");

    return fetch(`${url}`, {
      method: "POST",
      headers: this.backendHeaders(data.assistantId),
      body: JSON.stringify(data),
    });
  }

  /**
   * The widget token dengine minted for this thread, when one is known. Old
   * sessions have none: the request still goes out without the header and the
   * caller renders the generic error on a 401 rather than crashing.
   */
  private backendHeaders(assistantId: any): Record<string, string> {
    const headers: Record<string, string> = {
      "Access-Control-Allow-Origin": "*",
      "Content-Type": "application/json",
      Accept: "application/json",
    };

    const token = chatCache.getWidgetToken(assistantId);
    if (token) headers["X-Widget-Token"] = token;

    return headers;
  }

  public async ping(assitantId: any) {
    const baseUrl = this.getBaseUrl(assitantId, this.D_ENGINE);

    return await fetch(`${baseUrl}ping`, {
      method: "GET",
      headers: {
        "Access-Control-Allow-Origin": "*",
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    });
  }

  public async getWidgetConfiguration(drastikAssistantId: any) {
    const baseUrl = this.getBaseUrl(drastikAssistantId, this.BACKEND);
    const url = `${baseUrl}${process.env.REACT_APP_GET_WIDGET_CHAT_CONFIGURATION}?drastikAssistantId=${drastikAssistantId}`;

    return await fetch(`${url}`, {
      method: "GET",
      headers: {
        "Access-Control-Allow-Origin": "*",
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    });
  }

  /**
   * The hotel-configured copy for one locale.
   *
   * `language` is omitted entirely when the caller has none to name, which only
   * happens on the dedicated page opened without a `?lang=`. There `surface`
   * lets the backend answer from the assistant's configured standalone language
   * instead; either way the locale it served comes back as `servedLocale`.
   */
  public async getAssistantChatConfiguration(
    drastikAssistantId: any,
    language?: any,
    surface?: string
  ) {
    const baseUrl = this.getBaseUrl(drastikAssistantId, this.BACKEND);
    const query = [
      `drastikAssistantId=${encodeURIComponent(drastikAssistantId)}`,
      language ? `locale=${encodeURIComponent(language)}` : null,
      surface ? `surface=${encodeURIComponent(surface)}` : null,
    ]
      .filter(Boolean)
      .join("&");
    const url = `${baseUrl}${process.env.REACT_APP_GET_ASSISTANT_CHAT_CONFIGURATION}?${query}`;

    return await fetch(`${url}`, {
      method: "GET",
      headers: {
        "Access-Control-Allow-Origin": "*",
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    });
  }

  /**
   * Contract v2 native stream (W1a): POST /v2/stream with an encoded JSON body.
   * Replaces the legacy EventSource/GET path (message-in-URL). The response is a
   * text/event-stream that the caller reads via `response.body` (ReadableStream).
   *
   * `client_message_id` keys the run server-side for idempotency; on a reconnect
   * pass the same id plus `Last-Event-ID` to resume the buffered tail without
   * re-running the turn.
   */
  public async openV2Stream(
    params: {
      message: any;
      assistantId: any;
      threadId: any;
      language: any;
      fingerprint: any;
      clientMessageId: string;
      channel?: string;
    },
    lastEventId?: string | null
  ): Promise<Response> {
    const baseUrl = this.getBaseUrl(params.assistantId, this.D_ENGINE);

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
    };
    if (lastEventId) headers["Last-Event-ID"] = lastEventId;

    return await fetch(`${baseUrl}v2/stream`, {
      method: "POST",
      headers,
      body: JSON.stringify(
        withSessionContext({
          message: params.message,
          drastik_assistant_id: params.assistantId,
          thread_id: params.threadId ?? null,
          channel: params.channel ?? "WIDGET",
          language: params.language,
          fingerprint: params.fingerprint,
          client_message_id: params.clientMessageId,
        })
      ),
    });
  }

  private getBaseUrl(assistantId: any, server: any) {
    if (server === this.D_ENGINE) {
      if (ChatService.isHM(assistantId))
        return process.env.REACT_APP_IS_PROD !== "yes"
          ? process.env.REACT_APP_DENGINE_API_HM_DEV
          : process.env.REACT_APP_DENGINE_API_HM_PROD;

      return process.env.REACT_APP_IS_PROD !== "yes"
        ? process.env.REACT_APP_DENGINE_API_DEV
        : process.env.REACT_APP_DENGINE_API_PROD;
    } else if (server === this.BACKEND) {
      return process.env.REACT_APP_IS_PROD !== "yes"
        ? process.env.REACT_APP_API_DEV
        : process.env.REACT_APP_API_PROD;
    }
  }

  public static isHM(assistantId: any) {
    return IFH_ASSISTANTS.includes(assistantId);
  }

  public static isIodah(assistantId: any) {
    return assistantId === "138ce2b8-90db-4367-bb27-11b183d5f5e0";
  }
}
